import { useCallback, useEffect, useMemo, useRef } from 'react'
import type {
  Editor,
  JSONContent,
  MarkdownRendererHelpers,
} from '@tiptap/core'
import Image from '@tiptap/extension-image'
import Paragraph from '@tiptap/extension-paragraph'
import { TableKit } from '@tiptap/extension-table'
import { Markdown } from '@tiptap/markdown'
import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import { getComplianceImageProxyUrl } from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  replacePathsWithProxyUrls,
  replaceProxyUrlsWithPaths,
} from '@/shared/utils/compliance-document-images'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DocumentBar, DocumentMenuBar } from '@/components/document-menu-bar'
import { applyCompliancePlaceholderMarks } from '@/components/editor-marks/apply-compliance-placeholder-marks'
import { PlaceholderCalloutTrailingNode } from '@/components/editor-marks/placeholder-callout-trailing-node'
import {
  InsufficientInformationMark,
  InsufficientInformationPlugin,
} from '@/components/editor-marks/insufficient-information'
import {
  ManualInstructionMark,
  ManualInstructionPlugin,
} from '@/components/editor-marks/manual-instruction'
import {
  NotSpecifiedMark,
  NotSpecifiedPlugin,
} from '@/components/editor-marks/not-specified'
import { shouldShowDocumentEditorBubbleMenu } from '@/components/document-editor-bubble-menu'
import { normalizeEmptyTableCellsInMarkdown } from '@/components/normalize-empty-table-cells-in-markdown'

/** Empty paragraphs default to `&nbsp;` in TipTap markdown; keep table cells visually empty. */
const DocumentParagraph = Paragraph.extend({
  renderMarkdown: (
    node: JSONContent,
    helpers: MarkdownRendererHelpers
  ) => {
    if (!node) {
      return ''
    }

    const content = Array.isArray(node.content) ? node.content : []
    if (content.length === 0) {
      return ''
    }

    return helpers.renderChildren(content)
  },
})

const baseExtensions = [
  StarterKit.configure({
    paragraph: false,
  }),
  DocumentParagraph,
  TableKit,
  Markdown,
  NotSpecifiedMark,
  NotSpecifiedPlugin,
  ManualInstructionMark,
  ManualInstructionPlugin,
  InsufficientInformationMark,
  InsufficientInformationPlugin,
  PlaceholderCalloutTrailingNode,
]

type DocumentEditorProps = {
  markdown: string
  onSave?: (markdown: string) => Promise<void>
  disabled?: boolean
  isSaving?: boolean
  saveLabel?: string
  savingLabel?: string
  className?: string
  /** When provided, the TipTap editor instance is assigned when ready and cleared on unmount. */
  editorRef?: React.MutableRefObject<Editor | null | undefined>
  /** Optional node rendered in the toolbar row. */
  toolbarExtra?: React.ReactNode
  /** Optional content shown in a floating bubble menu when text is selected (e.g. Rewrite button). */
  bubbleMenuContent?: React.ReactNode
  /** When true, the bubble menu is not rendered (e.g. when a drawer opened from it is open). */
  bubbleMenuHidden?: boolean
  /** When provided, enables image upload for compliance documents. */
  imageUpload?: { complianceId: string }
  /**
   * When this value changes, the editor is always reset from `markdown` (e.g. after
   * server regenerate), even if the markdown string matches the last applied value or
   * the user has unsaved edits in TipTap.
   */
  contentSyncKey?: number
  /**
   * Merged into the TipTap/read-only body wrapper (default uses `py-3`). Use e.g.
   * `pt-0 pb-3` when a section title is rendered above the editor.
   */
  documentBodyClassName?: string
  /** When true, render editor body only (no Card chrome or document-level save bar). */
  embedded?: boolean
  /** Called when the TipTap editor instance is ready. */
  onEditorReady?: (editor: Editor) => void
  /** Called when the editor receives focus. */
  onEditorFocus?: () => void
  /** Called when the editor loses focus. */
  onEditorBlur?: () => void
  /** Called when document content changes (typing, formatting, etc.). */
  onContentChange?: () => void
  /** Called after placeholder marks are applied following a markdown sync. */
  onPlaceholderMarksApplied?: () => void
}

export function DocumentEditor({
  markdown,
  onSave,
  disabled = false,
  isSaving = false,
  saveLabel = 'Save',
  savingLabel = 'Saving…',
  className,
  editorRef,
  toolbarExtra,
  bubbleMenuContent,
  bubbleMenuHidden = false,
  imageUpload,
  contentSyncKey,
  documentBodyClassName,
  embedded = false,
  onEditorReady,
  onEditorFocus,
  onEditorBlur,
  onContentChange,
  onPlaceholderMarksApplied,
}: DocumentEditorProps) {
  const lastMarkdownRef = useRef<string>('')
  const lastContentSyncKeyRef = useRef<number | undefined>(undefined)
  const isSavingRef = useRef(false)
  const suppressContentChangeRef = useRef(true)
  const onContentChangeRef = useRef(onContentChange)
  const onEditorBlurRef = useRef(onEditorBlur)
  const onPlaceholderMarksAppliedRef = useRef(onPlaceholderMarksApplied)

  useEffect(() => {
    onContentChangeRef.current = onContentChange
  }, [onContentChange])

  useEffect(() => {
    onEditorBlurRef.current = onEditorBlur
  }, [onEditorBlur])

  useEffect(() => {
    onPlaceholderMarksAppliedRef.current = onPlaceholderMarksApplied
  }, [onPlaceholderMarksApplied])

  const initialContent = useMemo(() => {
    let raw = normalizeEmptyTableCellsInMarkdown(markdown.trim() || '')
    if (imageUpload != null && raw) {
      raw = replacePathsWithProxyUrls(raw, (path) =>
        getComplianceImageProxyUrl(imageUpload.complianceId, path)
      )
    }
    return raw
  }, [markdown, imageUpload])

  const documentBodyClass = useMemo(
    () =>
      cn(
        'tiptap min-h-[320px] w-full max-w-full overflow-x-auto overflow-y-clip px-4 py-3 outline-none max-w-none [&_pre]:overflow-x-auto',
        documentBodyClassName
      ),
    [documentBodyClassName]
  )

  const editor = useEditor(
    {
      extensions: [...baseExtensions, Image.configure({ allowBase64: true })],
      content: initialContent,
      contentType: 'markdown',
      editable: !disabled,
      immediatelyRender: false,
      editorProps: {
        attributes: {
          class: documentBodyClass,
        },
      },
      onUpdate: ({ transaction }) => {
        if (suppressContentChangeRef.current || !transaction.docChanged) {
          return
        }
        onContentChangeRef.current?.()
      },
    },
    [documentBodyClass]
  )

  useEffect(() => {
    if (editor == null) {
      return
    }
    let normalized = normalizeEmptyTableCellsInMarkdown(markdown.trim() || '')
    if (imageUpload != null && normalized) {
      normalized = replacePathsWithProxyUrls(normalized, (path) =>
        getComplianceImageProxyUrl(imageUpload.complianceId, path)
      )
    }
    const syncKeyChanged =
      contentSyncKey !== undefined &&
      contentSyncKey !== lastContentSyncKeyRef.current

    const editorWithMarkdown = editor as typeof editor & {
      getMarkdown?: () => string
      markdown?: { serialize: (json: unknown) => string }
    }
    let liveMarkdown = ''
    if (typeof editorWithMarkdown.getMarkdown === 'function') {
      liveMarkdown = normalizeEmptyTableCellsInMarkdown(
        editorWithMarkdown.getMarkdown().trim()
      )
    } else if (editorWithMarkdown.markdown != null) {
      liveMarkdown = normalizeEmptyTableCellsInMarkdown(
        editorWithMarkdown.markdown.serialize(editor.getJSON()).trim()
      )
    }
    if (imageUpload != null && liveMarkdown) {
      liveMarkdown = replacePathsWithProxyUrls(liveMarkdown, (path) =>
        getComplianceImageProxyUrl(imageUpload.complianceId, path)
      )
    }

    if (!syncKeyChanged && liveMarkdown === normalized) {
      lastMarkdownRef.current = normalized
      suppressContentChangeRef.current = false
      return
    }

    if (!syncKeyChanged && normalized === lastMarkdownRef.current) {
      suppressContentChangeRef.current = false
      return
    }

    if (isSavingRef.current && !syncKeyChanged) {
      return
    }

    if (syncKeyChanged) {
      lastContentSyncKeyRef.current = contentSyncKey
    }

    let cancelled = false
    suppressContentChangeRef.current = true
    queueMicrotask(() => {
      if (cancelled || editor.isDestroyed) {
        return
      }
      lastMarkdownRef.current = normalized
      editor.commands.setContent(normalized || '', {
        contentType: 'markdown',
        emitUpdate: false,
      })
      applyCompliancePlaceholderMarks(editor)
      onPlaceholderMarksAppliedRef.current?.()
      queueMicrotask(() => {
        if (!cancelled && !editor.isDestroyed) {
          suppressContentChangeRef.current = false
        }
      })
    })

    return () => {
      cancelled = true
      suppressContentChangeRef.current = true
    }
  }, [editor, markdown, imageUpload, contentSyncKey])

  // Sync editable state when disabled prop changes (e.g. during generation).
  useEffect(() => {
    if (editor == null) {
      return
    }
    editor.setEditable(!disabled)
  }, [editor, disabled])

  // Expose editor instance to parent via ref; clear on unmount.
  useEffect(() => {
    if (editorRef == null) {
      return
    }
    editorRef.current = editor ?? null
    return () => {
      editorRef.current = null
    }
  }, [editorRef, editor])

  useEffect(() => {
    if (editor == null || onEditorReady == null) {
      return
    }
    let cancelled = false
    queueMicrotask(() => {
      if (cancelled || editor.isDestroyed) {
        return
      }
      suppressContentChangeRef.current = true
      applyCompliancePlaceholderMarks(editor)
      onEditorReady(editor)
      queueMicrotask(() => {
        if (!cancelled && !editor.isDestroyed) {
          suppressContentChangeRef.current = false
        }
      })
    })
    return () => {
      cancelled = true
    }
  }, [editor, onEditorReady])

  useEffect(() => {
    if (editor == null || onEditorFocus == null) {
      return
    }
    const handleFocus = () => {
      onEditorFocus()
    }
    editor.on('focus', handleFocus)
    return () => {
      editor.off('focus', handleFocus)
    }
  }, [editor, onEditorFocus])

  useEffect(() => {
    if (editor == null) {
      return
    }
    const handleBlur = () => {
      onEditorBlurRef.current?.()
    }
    editor.on('blur', handleBlur)
    return () => {
      editor.off('blur', handleBlur)
    }
  }, [editor])

  const handleSave = useCallback(() => {
    if (editor == null || onSave == null) {
      return
    }
    const editorWithMarkdown = editor as typeof editor & {
      getMarkdown?: () => string
      markdown?: { serialize: (json: unknown) => string }
    }
    let currentMarkdown =
      typeof editorWithMarkdown.getMarkdown === 'function'
        ? editorWithMarkdown.getMarkdown()
        : editorWithMarkdown.markdown != null
          ? editorWithMarkdown.markdown.serialize(editor.getJSON())
          : ''
    currentMarkdown = normalizeEmptyTableCellsInMarkdown(currentMarkdown)
    if (imageUpload != null) {
      currentMarkdown = replaceProxyUrlsWithPaths(currentMarkdown)
    }
    isSavingRef.current = true
    void onSave(currentMarkdown).finally(() => {
      isSavingRef.current = false
    })
  }, [editor, onSave, imageUpload])

  if (editor == null) {
    return null
  }

  if (embedded) {
    return (
      <div className={className}>
        <EditorContent editor={editor} />
        {bubbleMenuContent != null && !bubbleMenuHidden && (
          <BubbleMenu
            editor={editor}
            shouldShow={shouldShowDocumentEditorBubbleMenu}
          >
            <div className='rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md'>
              {bubbleMenuContent}
            </div>
          </BubbleMenu>
        )}
      </div>
    )
  }

  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <div>
        {!disabled && (
          <DocumentMenuBar editor={editor} imageUpload={imageUpload} />
        )}
        <DocumentBar>
          {toolbarExtra}
          {onSave != null && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={disabled || isSaving}
              onClick={handleSave}
            >
              {isSaving ? savingLabel : saveLabel}
            </Button>
          )}
        </DocumentBar>
      </div>
      <div className='overflow-y-auto'>
        <EditorContent editor={editor} />
        {bubbleMenuContent != null && !bubbleMenuHidden && (
          <BubbleMenu
            editor={editor}
            shouldShow={shouldShowDocumentEditorBubbleMenu}
          >
            <div className='rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md'>
              {bubbleMenuContent}
            </div>
          </BubbleMenu>
        )}
      </div>
    </Card>
  )
}
